import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { WorkspaceDatabaseKeys } from '@database/workspace';
import { FormActionTypeEnum, PrimaryKeyValueModel } from '@shared';
import { combineLatest, first, map, Observable, of } from 'rxjs';
import { ValidationKeys } from '../models/validation-keys';
import { QueryParamsProvider } from '@domain/routing';
import { DataByTableTypeProvider, StandardTableProvider } from '@domain/business-layer';
import { ValidationsBuilderModel } from '../models';

export class UniqueSampleNumberBuilder {
	private validation: AsyncValidatorFn;
	private validationsMessages: string[] = [];

	public constructor(
		private entityName: string,
		private childTableName: string,
		private projectNumber: string,
		private columnName: string,
		private isChildTable: boolean,
		private formActionType: FormActionTypeEnum,
		private primaryKeys: string[],
		private queryParamsProvider: QueryParamsProvider,
		private dataByTableTypeProvider: DataByTableTypeProvider,
		private standardTableProvider: StandardTableProvider
	) {}

	public validate(): ValidationsBuilderModel | null {
		if (
			!this.isChildTable &&
			this.columnName === WorkspaceDatabaseKeys.columns.sampleNumber &&
			this.primaryKeys.includes(this.columnName)
		) {
			this.validation = this.uniqueSampleNumberValidation();
			this.validationsMessages.push(ValidationKeys.validationRules.uniqueSampleNumberRule);
			this.validationsMessages.push(ValidationKeys.validationRules.uniqueDeletedSampleNumberRule);
		}

		if (
			this.columnName === WorkspaceDatabaseKeys.columns.sampleNumber &&
			!this.primaryKeys.includes(this.columnName)
		) {
			this.validation = this.uniqueSampleNumberValidation();
			this.validationsMessages.push(ValidationKeys.validationRules.uniqueSampleNumberRule);
		}

		if (this.validation && this.validationsMessages) {
			return { validation: this.validation, messages: this.validationsMessages };
		}

		return null;
	}

	private uniqueSampleNumberValidation(): AsyncValidatorFn {
		return (control: AbstractControl): Observable<ValidationErrors | null> => {
			if (control?.value) {
				const group = control.parent;
				const primaryKeyValue: PrimaryKeyValueModel[] = this.primaryKeys.map(
					(pk: string) => ({ primaryKey: pk, primaryKeyValue: group?.get(pk).value }),
					[]
				);

				let excludedId = 0;

				if (this.formActionType === FormActionTypeEnum.edit) {
					excludedId = Number(
						this.queryParamsProvider.childEntryId
							? this.queryParamsProvider.childEntryId
							: this.queryParamsProvider.entryId
					);
				}

				return combineLatest([
					this.dataByTableTypeProvider.isSamplesTablePropertyValueUnique$(
						this.entityName,
						this.childTableName,
						this.standardTableProvider.standardTable,
						this.projectNumber,
						primaryKeyValue,
						this.columnName,
						WorkspaceDatabaseKeys.columns.standardNumber,
						control.value,
						excludedId,
						false
					),
					this.dataByTableTypeProvider.isSamplesTablePropertyValueUnique$(
						this.entityName,
						this.childTableName,
						this.standardTableProvider.standardTable,
						this.projectNumber,
						primaryKeyValue,
						this.columnName,
						WorkspaceDatabaseKeys.columns.standardNumber,
						control.value,
						excludedId,
						true
					)
				]).pipe(
					map(([isUniqueSampleNumber, isUniqueDeletedSampleNumber]) => {
						if (!isUniqueDeletedSampleNumber) {
							return { [ValidationKeys.validationRules.uniqueDeletedSampleNumberRule]: true };
						}

						if (!isUniqueSampleNumber) {
							return { [ValidationKeys.validationRules.uniqueSampleNumberRule]: true };
						}

						return null;
					}),
					first()
				);
			}
			return of(null);
		};
	}
}
